import type { SetupWorker } from 'msw/browser'

/** Global variable for worker setup */
let workerInstance: SetupWorker | null = null
/** Global variable for reloading state */
let isReloading = false
/** Global variable for ensuring state preventing race condition */
let isEnsuring = false

/** Initialize worker */
const startWorker = async () => {
  try {
    console.debug('### Worker[startWorker]: Starting MSW Service Worker [START] ###')

    // Check if the worker is already initialized
    if (!workerInstance) {
      console.debug('### Worker[startWorker]: Importing MSW worker ###')
      const { worker } = await import('./browser')
      workerInstance = worker // setup worker instance
    }

    console.debug('### Worker[startWorker]: MSW starting worker ###')
    await workerInstance.start({
      quiet: false, // Mostra i log MSW per debugging
      onUnhandledRequest(request) {
        const url = new URL(request.url)
        if (url.pathname.startsWith('/data/')) {
          return // Non fare nulla, lascia passare
        }
      },
    })
  } catch (error) {
    console.error('### Worker[startWorker]: Error starting MSW Service Worker ###', error)
    return
  } finally {
    console.debug('### Worker[startWorker]: Starting MSW Service Worker [END] ###')
  }
}

/** Ensure the MSW worker is running */
const ensureWorkerRunning = async () => {
  try {
    console.debug('### Worker[ensureWorkerRunning]: Ensuring MSW Service Worker is running [START] ###')

    if (isEnsuring) return // Prevent multiple concurrent checks

    isEnsuring = true // Set ensuring flag

    // Check if the service worker is controlling the page and has an active worker
    const registration = await navigator.serviceWorker.getRegistration()
    const hasController = !!navigator.serviceWorker.controller
    const hasActiveWorker = !!registration?.active

    // If not controlled or no active worker, start the worker
    if (!hasController || !hasActiveWorker) {
      console.warn('### Worker[ensureWorkerRunning]: MSW not controlling the page, restarting... ###')
      await startWorker()

      // After starting, check again if the worker is controlling the page
      if (!navigator.serviceWorker.controller && !isReloading) {
        console.warn('### Worker[ensureWorkerRunning]: MSW could not reattach. Reload required. ###')
        isReloading = true
        window.location.reload()
      }
    }
  } finally {
    isEnsuring = false // Reset ensuring flag
    console.debug('### Worker[ensureWorkerRunning]: Ensuring MSW Service Worker is running [END] ###')
  }
}

/** Listener visibility */
const setupVisibilityListener = () => {
  console.debug('### Worker[setupVisibilityListener]: Setting up visibility and online listeners [START] ###')

  document.addEventListener('visibilitychange', async () => {
    console.debug('### Worker[setupVisibilityListener]: Document visibility changed to:', document.visibilityState, ' ###')
    if (document.visibilityState === 'visible') {
      await ensureWorkerRunning()
    }
  })

  window.addEventListener('online', async () => {
    console.debug('### Worker[setupVisibilityListener]: Online Event ###')
    await ensureWorkerRunning()
  })

  window.addEventListener('focus', async () => {
    console.debug('### Worker[setupVisibilityListener]: Focus Event ###')
    await ensureWorkerRunning()
  })

  console.debug('### Worker[setupVisibilityListener]: Setting up visibility and online listeners [END] ###')
}

export const enableMocking = async () => {
  if (import.meta.env.PROD) return

  console.log('### Initializing MSW Service Worker ###')

  console.debug('### Worker[enableMocking]: Initializing MSW Service Worker [START] ###')

  await startWorker()

  setupVisibilityListener()

  console.debug('### Worker[enableMocking]: MSW Service Worker [END] ###')
}
